import { ProjectFormatError, parseProject, serializeProject } from '../core/model/serialize';
import { downloadBlob } from '../io/imageIO';
import type { AppContext } from './context';

/**
 * `.webpaint` project files: the whole document, layers and all, as JSON.
 *
 * Layers carry their pixels inline as data URLs, so a project is one
 * self-contained file with no sidecar assets — which is also what makes the
 * save → reload → load round trip provably lossless.
 */

export const PROJECT_EXTENSION = '.webpaint';
export const PROJECT_MIME = 'application/x-webpaint';

export function projectFileName(name: string): string {
  const trimmed = name.trim() || 'Untitled';
  return trimmed.toLowerCase().endsWith(PROJECT_EXTENSION)
    ? trimmed
    : `${trimmed}${PROJECT_EXTENSION}`;
}

export function isProjectFile(file: File): boolean {
  return (
    file.name.toLowerCase().endsWith(PROJECT_EXTENSION) ||
    file.type === PROJECT_MIME ||
    file.type === 'application/json'
  );
}

export async function saveProject(ctx: AppContext): Promise<string> {
  const doc = ctx.store.getState().doc;
  const fileName = projectFileName(doc.name);
  const blob = new Blob([serializeProject(doc)], { type: PROJECT_MIME });
  downloadBlob(blob, fileName);
  return fileName;
}

/** Load a project file, replacing the current document. */
export async function openProjectFile(file: File, ctx: AppContext): Promise<void> {
  const text = await file.text();
  try {
    const doc = parseProject(text);
    ctx.store.getState().loadDocument(doc);
    ctx.store.getState().zoomToFit();
  } catch (error) {
    if (error instanceof ProjectFormatError) {
      throw new Error(
        error.issues.length > 0 ? `${error.message} ${error.issues.join('; ')}` : error.message,
      );
    }
    throw error;
  }
}
